import { PreviewPanelDetailRow } from "components/Atoms/details/PreviewPanelDetailRow";
import StatusDropdown from "components/dropdowns/reactselect/StatusDropdown";
import { PreviewPanelInPageEditableField } from "components/MainSectionTemplate/InPageEditableField";
import { PreviewPanelSectionTemplate } from "components/MainSectionTemplate/PreviewPanelSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import Status from "components/Status/Status";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import moment from "moment";
import { ProductionDateRange } from "Production/components/ProductionDateRange";
import { ProductionFormState } from "Production/Detail/ProductionDetailSection";
import React, { useState } from "react";
import { ProductionPreviewPanelShowList } from "../PreviewPanelShowList";
import { PreviewPanelFreeFieldSections } from "../PreviewPanelFreeFieldSection";
import { ArtistPreview } from "../Company/CompanyPreviewPanel";

interface Props {
  production: server.dto.Production;
  freeFieldCategories: server.dto.FreeFieldCategorySmall[];
}

export const ProductionPreviewPanelContent = (props: Props) => {
  const hasMultipleStatuses = site.currentEnvironment.statuses.production.length > 1;
  const production = props.production;
  const [formState, setFormState] = useState<ProductionFormState>({
    name: production.name,
    status: production.status
  });

  const isInternal = site.currentEnvironment.type === "internal";
  const startMoment = moment.utc(production.startDate);
  const endMoment = moment.utc(production.endDate);

  const onValueChanged = (key: string, value: any) => {
    if (key === "name") {
      setFormState(prevState => ({
        ...prevState,
        name: value
      }));
    } else if (key === "status") {
      setFormState(prevState => ({
        ...prevState,
        status: value
      }));
    }
  }

  const getUpdateProductionInstruction = (state: ProductionFormState): server.dto.UpdateProductionDetailsInstruction => ({
    productionId: production.productionId,
    name: state.name,
    statusId: state.status.statusId,
  });

  return <PreviewPanelSectionTemplate
    enabled={production.permissions.canModify}
    onValueChanged={onValueChanged}
  >
    <PreviewPanelDetailRow labelTop={RESX.GeneralLabels.Name}>
      <PreviewPanelInPageEditableField
        field={{
          key: "name",
          render: () => <div>{formState.name}</div>,
          inPageEdit: {
            type: "confirmOrCancelButton",
            initialValue: formState.name,
            fullWidth: true,
            render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => (
              <RequiredTextInput
                validatorKey="name"
                focusOnMount
                maxLength={serverReference.constants.production.maxNameLength}
                value={value}
                onChange={(name) => { onChange(name); }}
                onEnter={() => submit()}
                onEsc={() => cancel()}
              />
            ),
            submitChange: (name: string) => window.CoreApi.Production.UpdateDetails(
              getUpdateProductionInstruction({ ...formState, name }))
          }
        }}
      />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.Status.resxStatus}>
      <PreviewPanelInPageEditableField
        field={{
          key: "status",
          render: () => <Status status={formState.status} />,
          hidden: !hasMultipleStatuses,
          inPageEdit: {
            render: ({ value, onChange }) => (
              <StatusDropdown
                value={value}
                onChange={onChange}
                mainArea="production"
                options={undefined}
                config={{
                  focusOnMount: true,
                  openMenuOnFocus: true
                }}
              />
            ),
            type: "custom",
            initialValue: formState.status,
            fullWidth: true,
            submitChange: (status: server.dto.StatusSmall) => window.CoreApi.Production.UpdateDetails(
              getUpdateProductionInstruction({ ...formState, status }))
          }
        }}
      />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={RESX.DateTime.lblDate}>
      <ProductionDateRange startDate={startMoment} endDate={endMoment} />
    </PreviewPanelDetailRow>

    <PreviewPanelDetailRow labelTop={site.currentEnvironment.functionGroups.artist.plural}>
      {production.artists.map(artist =>
        <div key={artist.artistId}>
          <ArtistPreview
            companyId={artist.artistId}
            trigger={<a>{artist.name}</a>}
          />
        </div>
      )}
    </PreviewPanelDetailRow>

    <PreviewPanelFreeFieldSections categories={props.freeFieldCategories} freefieldValues={production.freeFields} />

    <ProductionPreviewPanelShowList isInternal={isInternal} productionId={production.productionId} />
  </PreviewPanelSectionTemplate>;
}